import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ThemeToggle } from '../components/ThemeToggle';
import { ExerciseDetailDialog } from '../components/ExerciseDetailDialog';
import { ExerciseDemo } from '../components/ExerciseDemo';
import { ArrowLeft, Plus, Search, Dumbbell, Film } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = [
  { value: 'all', label: 'Wszystkie' },
  { value: 'strength', label: 'Siła' },
  { value: 'mobility', label: 'Mobilność' },
  { value: 'stretching', label: 'Stretching' },
  { value: 'breathing', label: 'Oddychanie' },
  { value: 'activation', label: 'Aktywacja' },
  { value: 'corrective', label: 'Korekcyjne' },
  { value: 'stability', label: 'Stabilizacja' },
  { value: 'cardio', label: 'Cardio' }
];

const DIFFICULTIES = [
  { value: 'all', label: 'Wszystkie' },
  { value: 'beginner', label: 'Początkujący' },
  { value: 'intermediate', label: 'Średniozaawansowany' },
  { value: 'advanced', label: 'Zaawansowany' }
];

const EQUIPMENT_OPTIONS = [
  { value: 'all', label: 'Cały sprzęt' },
  { value: 'none', label: 'Bez sprzętu' },
  { value: 'mat', label: 'Mata' },
  { value: 'bands', label: 'Taśmy' },
  { value: 'dumbbells', label: 'Hantle' },
  { value: 'kettlebell', label: 'Kettlebell' },
  { value: 'barbell', label: 'Sztanga' },
  { value: 'pullup_bar', label: 'Drążek' },
  { value: 'bench', label: 'Ławka' },
  { value: 'box', label: 'Skrzynia / stopień' },
  { value: 'cable', label: 'Wyciąg' },
  { value: 'machine', label: 'Maszyna' },
  { value: 'foam_roller', label: 'Foam roller' },
];

export const ExerciseLibraryPage = () => {
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [filteredExercises, setFilteredExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [equipmentFilter, setEquipmentFilter] = useState('all');
  const [reviewOnly, setReviewOnly] = useState(false); // generator metadata not yet checked by the trainer
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [newExercise, setNewExercise] = useState({
    name: '',
    description: '',
    category: 'strength',
    difficulty: 'beginner',
    muscles_targeted: '',
    equipment: '',
    contraindications: '',
    variations: ''
  });

  useEffect(() => {
    fetchExercises();
  }, []);

  useEffect(() => {
    filterExercises();
  }, [exercises, searchQuery, categoryFilter, difficultyFilter, equipmentFilter, reviewOnly]);

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/exercises`, { withCredentials: true });
      setExercises(response.data);
    } catch (error) {
      console.error('Error fetching exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterExercises = () => {
    let filtered = exercises;

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(ex => ex.category === categoryFilter);
    }

    if (difficultyFilter !== 'all') {
      filtered = filtered.filter(ex => ex.difficulty === difficultyFilter);
    }

    if (equipmentFilter !== 'all') {
      filtered = filtered.filter(ex => (ex.equipment || []).includes(equipmentFilter));
    }

    if (reviewOnly) {
      filtered = filtered.filter(ex => !ex.meta_reviewed);
    }

    if (searchQuery) {
      filtered = filtered.filter(ex =>
        ex.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ex.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredExercises(filtered);
  };

  const handleCreate = async () => {
    try {
      await axios.post(
        `${API_URL}/api/exercises`,
        {
          ...newExercise,
          muscles_targeted: newExercise.muscles_targeted.split(',').map(m => m.trim()).filter(Boolean),
          equipment: newExercise.equipment.split(',').map(e => e.trim()).filter(Boolean),
          contraindications: newExercise.contraindications.split(',').map(c => c.trim()).filter(Boolean),
          variations: newExercise.variations.split(',').map(v => v.trim()).filter(Boolean)
        },
        { withCredentials: true }
      );
      setDialogOpen(false);
      setNewExercise({
        name: '',
        description: '',
        category: 'strength',
        difficulty: 'beginner',
        muscles_targeted: '',
        equipment: '',
        contraindications: '',
        variations: ''
      });
      fetchExercises();
    } catch (error) {
      console.error('Error creating exercise:', error);
    }
  };

  const [matching, setMatching] = useState(false);
  const matchAnimations = async () => {
    setMatching(true);
    try {
      const res = await axios.post(`${API_URL}/api/exercises/match-animations`, {}, { withCredentials: true });
      const { matched, unmatched, total_with_animation } = res.data;
      toast.success(`Dopasowano ${matched.length} nowych animacji. Z animacją: ${total_with_animation}, bez dopasowania: ${unmatched.length}.`);
      fetchExercises();
    } catch (error) {
      console.error('Error matching animations:', error);
      toast.error('Nie udało się dopasować animacji');
    } finally {
      setMatching(false);
    }
  };

  const handleMetaSaved = (updated) => {
    setExercises(list => list.map(ex => (ex.id === updated.id ? updated : ex)));
    setSelectedExercise(updated);
  };

  const handleAnimationChange = (patch) => {
    setExercises(list => list.map(ex => (ex.id === selectedExercise.id ? { ...ex, ...patch } : ex)));
    setSelectedExercise(ex => ({ ...ex, ...patch }));
  };

  const getCategoryLabel = (value) => CATEGORIES.find(c => c.value === value)?.label || value;
  const getDifficultyLabel = (value) => DIFFICULTIES.find(d => d.value === value)?.label || value;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="exercise-library-page">
      <nav className="border-b border-[#2A2A2A] px-4 sm:px-8 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <Logo size="md" />
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="text-[#A1A1A1] hover:text-white px-2 sm:px-4"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Powrót</span>
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="mb-12">
          <h1 className="display-text text-5xl text-white mb-4">Biblioteka Ćwiczeń</h1>
          <p className="body-text text-[#A1A1A1] text-lg">
            {filteredExercises.length} ćwiczeń
          </p>
          <button type="button" onClick={() => setReviewOnly(v => !v)} data-testid="filter-review"
            className={`mt-4 text-sm px-4 py-1.5 rounded-full border ${reviewOnly ? 'border-[#C9A84C] text-[#C9A84C] bg-[#C9A84C]/10' : 'border-[#2A2A2A] text-[#A1A1A1]'}`}>
            Do przejrzenia dla generatora: {exercises.filter(ex => !ex.meta_reviewed).length}
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A1A1A1]" />
              <Input
                placeholder="Szukaj ćwiczeń..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-[#1E1E1E] border-[#2A2A2A] rounded-full text-white pl-12"
                data-testid="exercise-search-input"
              />
            </div>
          </div>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="bg-[#1E1E1E] border-[#2A2A2A] rounded-full text-white" data-testid="filter-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1E1E1E] border-[#2A2A2A] text-white">
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={difficultyFilter} onValueChange={setDifficultyFilter}>
            <SelectTrigger className="bg-[#1E1E1E] border-[#2A2A2A] rounded-full text-white" data-testid="filter-difficulty">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1E1E1E] border-[#2A2A2A] text-white">
              {DIFFICULTIES.map(diff => (
                <SelectItem key={diff.value} value={diff.value}>{diff.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={equipmentFilter} onValueChange={setEquipmentFilter}>
            <SelectTrigger className="bg-[#1E1E1E] border-[#2A2A2A] rounded-full text-white" data-testid="filter-equipment">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1E1E1E] border-[#2A2A2A] text-white">
              {EQUIPMENT_OPTIONS.map(eq => (
                <SelectItem key={eq.value} value={eq.value}>{eq.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap justify-end gap-3 mb-8">
          <Button
            onClick={matchAnimations}
            disabled={matching}
            variant="ghost"
            className="text-[#A1A1A1] hover:text-white border border-[#2A2A2A] rounded-full"
            data-testid="match-animations-button"
          >
            <Film className="w-4 h-4 mr-2" />
            {matching ? 'Dopasowuję...' : 'Dopasuj animacje'}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full"
                data-testid="add-exercise-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Dodaj ćwiczenie
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1E1E1E] border-[#2A2A2A] rounded-3xl max-w-2xl">
              <DialogHeader>
                <DialogTitle className="heading-text text-2xl text-white">Nowe ćwiczenie</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">NAZWA</Label>
                  <Input
                    value={newExercise.name}
                    onChange={(e) => setNewExercise({ ...newExercise, name: e.target.value })}
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                  />
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">OPIS</Label>
                  <Textarea
                    value={newExercise.description}
                    onChange={(e) => setNewExercise({ ...newExercise, description: e.target.value })}
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="label-text text-[#A1A1A1] mb-2 block">KATEGORIA</Label>
                    <Select
                      value={newExercise.category}
                      onValueChange={(value) => setNewExercise({ ...newExercise, category: value })}
                    >
                      <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E1E1E] border-[#2A2A2A] text-white">
                        {CATEGORIES.filter(c => c.value !== 'all').map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-text text-[#A1A1A1] mb-2 block">TRUDNOŚĆ</Label>
                    <Select
                      value={newExercise.difficulty}
                      onValueChange={(value) => setNewExercise({ ...newExercise, difficulty: value })}
                    >
                      <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E1E1E] border-[#2A2A2A] text-white">
                        {DIFFICULTIES.filter(d => d.value !== 'all').map(diff => (
                          <SelectItem key={diff.value} value={diff.value}>{diff.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">MIĘŚNIE (PRZECINEK)</Label>
                  <Input
                    value={newExercise.muscles_targeted}
                    onChange={(e) => setNewExercise({ ...newExercise, muscles_targeted: e.target.value })}
                    placeholder="np. Klatka piersiowa, Triceps"
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                  />
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">SPRZĘT (PRZECINEK)</Label>
                  <Input
                    value={newExercise.equipment}
                    onChange={(e) => setNewExercise({ ...newExercise, equipment: e.target.value })}
                    placeholder="np. dumbbells, mat, barbell, none"
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                    data-testid="exercise-equipment-input"
                  />
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">PRZECIWWSKAZANIA (PRZECINEK)</Label>
                  <Input
                    value={newExercise.contraindications}
                    onChange={(e) => setNewExercise({ ...newExercise, contraindications: e.target.value })}
                    placeholder="np. uraz barku, ostra przepuklina"
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                  />
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">WARIANTY (PRZECINEK)</Label>
                  <Input
                    value={newExercise.variations}
                    onChange={(e) => setNewExercise({ ...newExercise, variations: e.target.value })}
                    placeholder="np. wersja z hantlami, wariant jednonóż"
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                  />
                </div>
                <Button
                  onClick={handleCreate}
                  disabled={!newExercise.name || !newExercise.description}
                  className="w-full bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full"
                >
                  Utwórz ćwiczenie
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Exercise Grid */}
        {loading ? (
          <div className="text-center text-[#A1A1A1] py-12">Ładowanie...</div>
        ) : filteredExercises.length === 0 ? (
          <div className="card-surface rounded-3xl p-12 text-center">
            <Dumbbell className="w-16 h-16 text-[#A1A1A1] mx-auto mb-4" />
            <p className="body-text text-[#A1A1A1] text-lg">Brak ćwiczeń</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredExercises.map(exercise => (
              <div
                key={exercise.id}
                onClick={() => setSelectedExercise(exercise)}
                className="card-surface card-surface-hover rounded-3xl p-6 cursor-pointer"
                data-testid={`exercise-card-${exercise.id}`}
              >
                {exercise.animation_url && <ExerciseDemo exercise={exercise} animate={false} className="mb-4 rounded-2xl" />}
                <div className="mb-4">
                  <h3 className="heading-text text-xl text-white mb-2">{exercise.name}</h3>
                  <p className="body-text text-[#A1A1A1] text-sm line-clamp-2">
                    {exercise.description}
                  </p>
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <span className="label-text text-[#C9A84C] text-[0.65rem]">
                    {getCategoryLabel(exercise.category)}
                  </span>
                  <span className="label-text text-[#A1A1A1] text-[0.65rem]">
                    {getDifficultyLabel(exercise.difficulty)}
                  </span>
                </div>

                {exercise.muscles_targeted.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {exercise.muscles_targeted.slice(0, 3).map((muscle, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-[#0A0A0A] rounded-full text-xs text-[#A1A1A1]"
                      >
                        {muscle}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      <ExerciseDetailDialog
        exercise={selectedExercise}
        open={!!selectedExercise}
        onOpenChange={(open) => !open && setSelectedExercise(null)}
        onAnimationChange={handleAnimationChange}
        onMetaSaved={handleMetaSaved}
        allExercises={exercises}
      />
    </div>
  );
};